/**
 * Visor de la constancia de pago (captura de Yape/Plin o PDF).
 *
 * La descarga con el token en la cabecera y la muestra desde un blob local: el
 * comprobante es dato sensible y no puede quedar accesible en una URL que se
 * abra sin sesión.
 */
import { useEffect, useState } from "react";
import { Download, FileText } from "lucide-react";
import { fetchConstancia } from "../api/pagos";
import { Alert, Dialog } from "./ui";

export function ConstanciaViewer({
  pagoId,
  titulo,
  onClose,
}: {
  /** null = cerrado. */
  pagoId: number | null;
  titulo: string;
  onClose: () => void;
}) {
  const [estado, setEstado] = useState<
    { fase: "cargando" } | { fase: "ok"; url: string; tipo: string } | { fase: "error" }
  >({ fase: "cargando" });

  useEffect(() => {
    if (pagoId == null) return;
    let vigente = true;
    let creada: string | null = null;
    setEstado({ fase: "cargando" });
    fetchConstancia(pagoId)
      .then(({ url, tipo }) => {
        creada = url;
        if (vigente) setEstado({ fase: "ok", url, tipo });
        else URL.revokeObjectURL(url);
      })
      .catch(() => vigente && setEstado({ fase: "error" }));
    return () => {
      vigente = false;
      // El blob vive en memoria hasta que lo soltamos.
      if (creada) URL.revokeObjectURL(creada);
    };
  }, [pagoId]);

  return (
    <Dialog
      open={pagoId != null}
      onClose={onClose}
      title="Constancia del pago"
      description={titulo}
      size="lg"
    >
      {estado.fase === "cargando" && (
        <p className="py-10 text-center text-sm text-slate-400">Cargando…</p>
      )}
      {estado.fase === "error" && (
        <Alert>No se pudo cargar la constancia.</Alert>
      )}
      {estado.fase === "ok" && (
        <div className="space-y-3">
          {estado.tipo === "application/pdf" ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 py-10">
              <FileText className="h-10 w-10 text-slate-400" />
              <p className="text-sm text-slate-600">La constancia es un PDF.</p>
            </div>
          ) : (
            <img
              src={estado.url}
              alt="Constancia de pago"
              className="mx-auto max-h-[65vh] rounded-lg border border-slate-200 object-contain"
            />
          )}
          <div className="flex justify-end">
            <a
              href={estado.url}
              download={`constancia-${pagoId}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-[color:var(--color-link)] hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
            >
              <Download className="h-4 w-4" />
              Abrir en tamaño completo
            </a>
          </div>
        </div>
      )}
    </Dialog>
  );
}
